import React, { useEffect, useState } from "react";
import Axios from "../../_services/caller.service";
import styles from "./ManageCO.module.scss";
import { CO_FREQUENCIES, CO_RETVF_CORRIGE_CF, CO_NIVEAU_ENTREE, computeCoCorrection, CO_VOCAL_RETVF, CO_VOCAL_NIVEAU_ENTREE, computeVocalCoCorrection } from "./configCO";

import InputForms from "../../components/UiComponents/forms/InputForms";
import { FiRefreshCcw, FiChevronDown, FiChevronUp, FiEdit2, FiTrash2 } from "react-icons/fi";
import { BsFillExclamationTriangleFill } from "react-icons/bs";
import { IoMdAddCircle } from "react-icons/io";
import Button from "../../components/UiComponents/Button";
import Modal from "../../components/UiComponents/modals/Modal";

import useModal from "../../hooks/useModal";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "../superAdmin/managePermissions/PermissionGate";

const ManageCO = () => {
  const { t } = useTranslation();
  const [calibrations, setCalibrations] = useState([]);
  const [filteredCalibrations, setFilteredCalibrations] = useState([]);
  const [maj, setMaj] = useState(false);

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // EXPANDED ROWS (to show frequency detail)
  const [expandedRows, setExpandedRows] = useState({});

  // EDITING ROWS
  const [editingRows, setEditingRows] = useState({});
  const [editingSonometerMeasures, setEditingSonometerMeasures] = useState({});
  const [editingVocalSonometerMeasures, setEditingVocalSonometerMeasures] = useState({});

  // ASSIGNED CABINES per CO serial: { [serial]: [{ cabId, cabName }] }
  const [assignedByCo, setAssignedByCo] = useState({});

  // ADD FORM
  const [newSerial, setNewSerial] = useState("");
  const [newSoundcardSerial, setNewSoundcardSerial] = useState("");
  const [newCalibTonal, setNewCalibTonal] = useState(
    CO_FREQUENCIES.map(() => "-200")
  );
  const [newCalibVocal, setNewCalibVocal] = useState("-200");
  const [newSonometerMeasures, setNewSonometerMeasures] = useState(
    CO_FREQUENCIES.map(() => "")
  );
  const [newVocalSonometerMeasures, setNewVocalSonometerMeasures] = useState("");

  // MODAL
  const { isShowing, toggle } = useModal();
  const [activeForm, setActiveForm] = useState(null);
  const [currentUpdate, setCurrentUpdate] = useState(null);

  const customToggle = () => {
    setActiveForm(null);
    toggle();
  };

  // GET ALL CO CALIBRATIONS
  useEffect(() => {
    Axios.get("/getCoCalibrations")
      .then((response) => {
        setCalibrations(response.data.results);
        setFilteredCalibrations(response.data.results);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [maj]);

  // GET ASSIGNED CO -> CABINES MAP
  useEffect(() => {
    Axios.get("/getAssignedCoSerials")
      .then((response) => {
        setAssignedByCo(response.data.grouped || {});
      })
      .catch((error) => {
        console.log(error);
      });
  }, [maj]);

  // SEARCH
  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    const searched = calibrations.filter(
      (co) =>
        co.serial.toLowerCase().includes(term) ||
        co.soundcard_serial.toLowerCase().includes(term)
    );
    setFilteredCalibrations(searched);
    setCurrentPage(1);
  };

  // REFRESH FILTER
  const refreshFilter = () => {
    setFilteredCalibrations(calibrations);
    setItemsPerPage(10);
    setSearchTerm("");
    setCurrentPage(1);
  };

  // EXPAND / COLLAPSE ROW
  const toggleExpand = (serial) => {
    setExpandedRows((prev) => ({ ...prev, [serial]: !prev[serial] }));
  };

  // TOGGLE EDIT MODE
  const toggleEdit = (serial) => {
    setEditingRows((prev) => {
      const isEditing = !prev[serial];
      // When entering edit mode, also expand the row to show frequencies
      if (isEditing) {
        setExpandedRows((p) => ({ ...p, [serial]: true }));
      }
      return { ...prev, [serial]: isEditing };
    });
  };

  // CANCEL EDIT — restore original values
  const cancelEdit = (originalSerial) => {
    setFilteredCalibrations((prev) =>
      prev.map((co) => {
        const original = calibrations.find((c) => c.serial === originalSerial);
        if (original && co.id === original.id) {
          return { ...original };
        }
        if (co.serial === originalSerial) {
          return original ? { ...original } : co;
        }
        return co;
      })
    );
    setEditingRows((prev) => ({ ...prev, [originalSerial]: false }));
    setEditingSonometerMeasures((prev) => { const c = { ...prev }; delete c[originalSerial]; return c; });
    setEditingVocalSonometerMeasures((prev) => { const c = { ...prev }; delete c[originalSerial]; return c; });
  };

  // HANDLE INLINE EDIT
  const handleInputChange = (serial, field, value) => {
    setFilteredCalibrations((prev) =>
      prev.map((co) => {
        if (co.serial === serial) {
          if (field === "calib_vocal" || field === "soundcard_serial" || field === "serial") {
            return { ...co, [field]: value };
          }
          // For tonal: field is index
          if (typeof field === "number") {
            const tonal = co.calib_tonal.split(",");
            tonal[field] = value;
            return { ...co, calib_tonal: tonal.join(",") };
          }
        }
        return co;
      })
    );
  };

  // UPDATE CO
  const handleUpdate = (originalSerial) => {
    const original = calibrations.find((c) => c.serial === originalSerial);
    const editedCo = original
      ? filteredCalibrations.find((c) => c.id === original.id)
      : filteredCalibrations.find((c) => c.serial === originalSerial);
    setCurrentUpdate({ ...editedCo, _originalSerial: originalSerial });
    setActiveForm("warningChangeValueCO");
    toggle();
  };

  const confirmUpdate = () => {
    if (!currentUpdate) return;
    const originalSerial = currentUpdate._originalSerial || currentUpdate.serial;
    Axios.put("/updateCoCalibration", {
      serial: currentUpdate.serial,
      originalSerial: originalSerial,
      soundcardSerial: currentUpdate.soundcard_serial,
      calibTonal: currentUpdate.calib_tonal,
      calibVocal: currentUpdate.calib_vocal,
      dateCalibration: currentUpdate.date_calibration,
    })
      .then(() => {
        toast.success(t("co.co_updated", "Calibration CO mise à jour"));
        setEditingRows((prev) => { const updated = { ...prev }; delete updated[originalSerial]; return updated; });
        setEditingSonometerMeasures((prev) => { const c = { ...prev }; delete c[originalSerial]; return c; });
        setEditingVocalSonometerMeasures((prev) => { const c = { ...prev }; delete c[originalSerial]; return c; });
        setCurrentUpdate(null);
        setMaj(!maj);
        customToggle();
      })
      .catch((error) => {
        console.log(error);
        toast.error(
          error.response?.data?.message || t("common.error", "Erreur")
        );
      });
  };

  // DELETE CO
  const handleDelete = (serial) => {
    setCurrentUpdate({ serial });
    setActiveForm("confirmDeleteCO");
    toggle();
  };

  // UNLINK CO from a single cabine
  const handleUnlinkCabine = (serial, cabId, cabName) => {
    setCurrentUpdate({ serial, _cabId: cabId, _cabName: cabName });
    setActiveForm("confirmUnlinkCabine");
    toggle();
  };

  const confirmUnlink = () => {
    if (!currentUpdate) return;
    Axios.post("/unlinkCoFromCabine", {
      serial: currentUpdate.serial,
      cabId: currentUpdate._cabId,
    })
      .then(() => {
        toast.success(
          t("co.unlink_success", "Liaison CO/cabine supprimée")
        );
        setCurrentUpdate(null);
        setMaj(!maj);
        customToggle();
      })
      .catch((error) => {
        console.log(error);
        toast.error(
          error.response?.data?.message || t("common.error", "Erreur")
        );
      });
  };

  const confirmDelete = () => {
    if (!currentUpdate) return;
    Axios.delete(`/deleteCoCalibration/${encodeURIComponent(currentUpdate.serial)}`)
      .then(() => {
        toast.success(t("co.co_deleted", "Calibration CO supprimée"));
        setCurrentUpdate(null);
        setMaj(!maj);
        customToggle();
      })
      .catch((error) => {
        console.log(error);
        toast.error(
          error.response?.data?.message || t("common.error", "Erreur")
        );
      });
  };

  // ADD NEW CO
  const addNewCO = () => {
    if (!newSerial.trim()) {
      toast.error(
        t(
          "co.serial_required",
          "Veuillez fournir un numéro de série"
        )
      );
      return;
    }

    const today = new Date().toISOString().split("T")[0];

    Axios.post("/addCoCalibration", {
      serial: newSerial.trim(),
      soundcardSerial: newSoundcardSerial.trim(),
      calibTonal: newCalibTonal.join(","),
      calibVocal: newCalibVocal,
      dateCalibration: today,
    })
      .then((response) => {
        if (response.data.error) {
          toast.error(response.data.message);
        } else {
          toast.success(
            t("co.new_co_success", "Calibration CO ajoutée avec succès")
          );
          // Reset form
          setNewSerial("");
          setNewSoundcardSerial("");
          setNewCalibTonal(CO_FREQUENCIES.map(() => "-200"));
          setNewCalibVocal("-200");
          setNewSonometerMeasures(CO_FREQUENCIES.map(() => ""));
          setNewVocalSonometerMeasures("");
          setMaj(!maj);
          customToggle();
        }
      })
      .catch((error) => {
        if (error.response) {
          toast.error(error.response.data.message);
        } else {
          toast.error(t("common.no_server_response", "Pas de réponse du serveur"));
        }
      });
  };

  // PAGINATION
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filteredCalibrations.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredCalibrations.length / itemsPerPage);

  return (
    <>
      <div className={styles.page}>
        <div className={styles.header}>
          <h2>{t("co.manage_title", "Gestion des Calibrations CO")}</h2>
        </div>

        <div className={styles.moduleCard}>
          <div className={styles.containerSearchAndFilter}>
            <input
              type="text"
              className={styles.searchInput}
              onChange={handleSearch}
              value={searchTerm}
              placeholder={t(
                "co.search_serial",
                "Rechercher par n° de série..."
              )}
            />
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className={styles.selectUserPerPage}
            >
              {[5, 10, 20, 30, 50].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>

            <button
              type="button"
              className={styles.iconButton}
              onClick={refreshFilter}
            >
              <FiRefreshCcw /> Refresh
            </button>

            <PermissionGate actionKey="ManageCO.addCO">
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => {
                  setActiveForm("addNewCO");
                  toggle();
                }}
              >
                <IoMdAddCircle />{" "}
                {t("co.add_co", "Ajouter une calibration CO")}
              </button>
            </PermissionGate>
          </div>

          {/* ═══ TABLE DES CO ═══ */}
          <div className={styles.tableWrapper}>
            <table className={styles.coTable}>
              <thead>
                <tr>
                  <th></th>
                  <th>{t("co.serial", "N° Série CO")}</th>
                  <th>{t("co.soundcard_serial", "N° Série Carte Son")}</th>
                  <th>{t("co.vocal", "Vocal")}</th>
                  <th>{t("co.date", "Date Calibration")}</th>
                  <th>{t("co.assigned_cabines", "Cabines attribuées")}</th>
                  <th className={styles.colAction}>
                    {t("co.action", "Action")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((co) => {
                  const isEditing = editingRows[co.serial];
                  const tonalValues = co.calib_tonal ? co.calib_tonal.split(",") : [];
                  return (
                  <React.Fragment key={co.id || co.serial}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          className={styles.expandBtn}
                          onClick={() => toggleExpand(co.serial)}
                        >
                          {expandedRows[co.serial] ? (
                            <FiChevronUp />
                          ) : (
                            <FiChevronDown />
                          )}
                        </button>
                      </td>
                      <td className={styles.serial}>
                        {isEditing ? (
                          <input
                            type="text"
                            className={styles.freqInput}
                            style={{ width: "140px" }}
                            value={co.serial}
                            onChange={(e) => {
                              const origSerial = calibrations.find((c) => c.id === co.id)?.serial || co.serial;
                              setFilteredCalibrations((prev) =>
                                prev.map((c) =>
                                  (c.id === co.id || c.serial === origSerial)
                                    ? { ...c, serial: e.target.value }
                                    : c
                                )
                              );
                            }}
                          />
                        ) : (
                          co.serial
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            className={styles.freqInput}
                            style={{ width: "120px" }}
                            value={co.soundcard_serial}
                            onChange={(e) =>
                              handleInputChange(
                                co.serial,
                                "soundcard_serial",
                                e.target.value
                              )
                            }
                          />
                        ) : (
                          <span>{co.soundcard_serial || "-"}</span>
                        )}
                      </td>
                      <td>
                        <span>{co.calib_vocal || "0"}</span>
                      </td>
                      <td className={styles.dateCell}>
                        {co.date_calibration || "-"}
                      </td>
                      <td>
                        {(() => {
                          const cabs = assignedByCo[co.serial] || [];
                          if (cabs.length === 0) {
                            return (
                              <span className={styles.noCabBadge}>
                                {t("co.not_assigned", "Non attribué")}
                              </span>
                            );
                          }
                          return (
                            <div className={styles.cabBadges}>
                              {cabs.map((c) => (
                                <span key={c.cabId} className={styles.cabBadge} title={`ID: ${c.cabId}`}>
                                  {c.cabName || `Cab #${c.cabId}`}
                                  <PermissionGate actionKey="ManageCO.deleteCO">
                                    <button
                                      type="button"
                                      className={styles.cabBadgeRemove}
                                      onClick={() => handleUnlinkCabine(co.serial, c.cabId, c.cabName)}
                                      title={t("co.unlink_cabine", "Supprimer la liaison avec cette cabine")}
                                    >
                                      ×
                                    </button>
                                  </PermissionGate>
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                      <td className={styles.colAction}>
                        <div className={styles.actionButtons}>
                          {isEditing ? (
                            <>
                              <PermissionGate actionKey="ManageCO.updateCO">
                                <button
                                  type="button"
                                  className={styles.updateButton}
                                  onClick={() => {
                                    const origSerial = calibrations.find((c) => c.id === co.id)?.serial || co.serial;
                                    handleUpdate(origSerial);
                                  }}
                                >
                                  {t("co.save", "Sauvegarder")}
                                </button>
                              </PermissionGate>
                              <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={() => {
                                  const origSerial = calibrations.find((c) => c.id === co.id)?.serial || co.serial;
                                  cancelEdit(origSerial);
                                }}
                              >
                                {t("common.cancel", "Annuler")}
                              </button>
                            </>
                          ) : (
                            <>
                              <PermissionGate actionKey="ManageCO.updateCO">
                                <button
                                  type="button"
                                  className={styles.editButton}
                                  onClick={() => toggleEdit(co.serial)}
                                >
                                  <FiEdit2 /> {t("co.edit", "Éditer")}
                                </button>
                              </PermissionGate>
                              <PermissionGate actionKey="ManageCO.deleteCO">
                                <button
                                  type="button"
                                  className={styles.deleteButton}
                                  onClick={() => handleDelete(co.serial)}
                                >
                                  <FiTrash2 /> {t("co.delete", "Supprimer")}
                                </button>
                              </PermissionGate>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>

                    {expandedRows[co.serial] && (
                      <tr className={styles.expandedRow}>
                        <td colSpan={7}>
                          {isEditing ? (
                            <div className={styles.sonometerTableWrapper}>
                              <table className={styles.sonometerTable}>
                                <thead>
                                  <tr>
                                    <th></th>
                                    {CO_FREQUENCIES.map((freq) => (
                                      <th key={freq}>{freq}</th>
                                    ))}
                                    <th>{t("co.vocal", "Vocal")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td className={styles.editRowLabel}>{t("co.sonometer_measure", "Mesure sono.")}</td>
                                    {CO_FREQUENCIES.map((freq, idx) => (
                                      <td key={freq}>
                                        <input
                                          type="number"
                                          step="0.1"
                                          className={styles.sonoInput}
                                          value={editingSonometerMeasures[co.serial]?.[idx] ?? ""}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setEditingSonometerMeasures((prev) => {
                                              const arr = [...(prev[co.serial] || CO_FREQUENCIES.map(() => ""))];
                                              arr[idx] = val;
                                              return { ...prev, [co.serial]: arr };
                                            });
                                            handleInputChange(co.serial, idx, computeCoCorrection(val, idx) || "-200");
                                          }}
                                        />
                                      </td>
                                    ))}
                                    <td>
                                      <input
                                        type="number"
                                        step="0.1"
                                        className={styles.sonoInput}
                                        value={editingVocalSonometerMeasures[co.serial] ?? ""}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setEditingVocalSonometerMeasures((prev) => ({ ...prev, [co.serial]: val }));
                                          handleInputChange(co.serial, "calib_vocal", computeVocalCoCorrection(val, CO_VOCAL_RETVF) || "-200");
                                        }}
                                      />
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className={styles.editRowLabel}>{t("co.correction", "Correction")}</td>
                                    {CO_FREQUENCIES.map((freq, idx) => (
                                      <td key={freq} className={styles.correctionCell}>
                                        {tonalValues[idx] || "-200"}
                                      </td>
                                    ))}
                                    <td className={styles.correctionCell}>
                                      {co.calib_vocal || "-200"}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className={styles.freqDetail}>
                              {CO_FREQUENCIES.map((freq, idx) => (
                                <div key={freq} className={styles.freqDetailItem}>
                                  <span>{freq}</span>
                                  <span className={styles.freqValue}>{tonalValues[idx] || "0"}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className={styles.containerPagination}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              {t("mics.first_page", "Première")}
            </button>
            <button
              onClick={() => {
                if (currentPage > 1) setCurrentPage(currentPage - 1);
              }}
              disabled={currentPage === 1}
            >
              {t("mics.previous", "Précédent")}
            </button>
            <button
              onClick={() => {
                if (currentPage < totalPages) setCurrentPage(currentPage + 1);
              }}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              {t("mics.next", "Suivant")}
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              {t("mics.last_page", "Dernière")}
            </button>
          </div>
          <div className={styles.numberPage}>
            {t("mics.page_of", {
              current: currentPage,
              total: totalPages || 1,
              defaultValue: `Page ${currentPage} sur ${totalPages || 1}`,
            })}
          </div>
        </div>

        {/* ═══ MODALS ═══ */}
        <Modal isShowing={isShowing} hide={customToggle} className={styles.wideModal}>
          {activeForm === "addNewCO" && (
            <div className={styles.containerModal}>
              <h2>{t("co.add_co", "Ajouter une calibration CO")}</h2>

              <div className={styles.inputWrapper}>
                <InputForms
                  onChange={(e) => setNewSerial(e.target.value)}
                  value={newSerial}
                  placeholder={t("co.serial", "N° Série CO")}
                />
              </div>
              <div className={styles.inputWrapper}>
                <InputForms
                  onChange={(e) => setNewSoundcardSerial(e.target.value)}
                  value={newSoundcardSerial}
                  placeholder={t("co.soundcard_serial", "N° Série Carte Son")}
                />
              </div>

              <h3 className={styles.sectionTitle}>
              
              </h3>

              <div className={styles.sonometerTableWrapper}>
                <table className={styles.sonometerTable}>
                  <thead>
                    <tr>
                      <th>{t("co.freq", "Fréquence")}</th>
                      <th>{t("co.niveau_entree", "Niveau entrée")}</th>
                      <th>{t("co.retvf", "RETVF corrigé CF")}</th>
                      <th>{t("co.sonometer_measure", "Mesure sonomètre")}</th>
                      <th>{t("co.correction", "Correction (→ BDD)")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className={styles.vocalSeparatorRow}>
                      <td colSpan={5}>{t("co.tonal_values", "Tonal")}</td>
                    </tr>
                    {CO_FREQUENCIES.map((freq, idx) => {
                      const niveau = CO_NIVEAU_ENTREE[idx];
                      const retvf = CO_RETVF_CORRIGE_CF[idx];
                      const correction = newCalibTonal[idx];
                      const hasMeasure = newSonometerMeasures[idx] !== "" && !Number.isNaN(parseFloat(newSonometerMeasures[idx]));
                      return (
                        <tr key={freq}>
                          <td className={styles.freqCell}>{freq}</td>
                          <td className={styles.refCell}>{niveau}</td>
                          <td className={styles.refCell}>{retvf}</td>
                          <td>
                            <input
                              type="number"
                              step="0.1"
                              className={styles.sonoInput}
                              value={newSonometerMeasures[idx]}
                              onChange={(e) => {
                                const updatedMeasures = [...newSonometerMeasures];
                                updatedMeasures[idx] = e.target.value;
                                setNewSonometerMeasures(updatedMeasures);
                                const updatedCorrections = [...newCalibTonal];
                                updatedCorrections[idx] = computeCoCorrection(e.target.value, idx) || "-200";
                                setNewCalibTonal(updatedCorrections);
                              }}
                            />
                          </td>
                          <td className={styles.correctionCell}>
                            {hasMeasure ? correction : "-200"}
                          </td>
                        </tr>
                      );
                    })}

                    <tr className={styles.vocalSeparatorRow}>
                      <td colSpan={5}>{t("co.vocal", "Vocal")}</td>
                    </tr>

                    <tr className={styles.vocalRow}>
                      <td className={styles.freqCell}>Vocal</td>
                      <td className={styles.refCell}>{CO_VOCAL_NIVEAU_ENTREE}</td>
                      <td className={styles.refCell}>{CO_VOCAL_RETVF}</td>
                      <td>
                        <input
                          type="number"
                          step="0.1"
                          className={styles.sonoInput}
                          value={newVocalSonometerMeasures}
                          onChange={(e) => {
                            setNewVocalSonometerMeasures(e.target.value);
                            setNewCalibVocal(computeVocalCoCorrection(e.target.value, CO_VOCAL_RETVF) || "-200");
                          }}
                        />
                      </td>
                      <td className={styles.correctionCell}>
                        {newVocalSonometerMeasures !== "" && !Number.isNaN(parseFloat(newVocalSonometerMeasures)) ? newCalibVocal : "-200"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                className={styles.modalSaveBtn}
                onClick={addNewCO}
              >
                {t("common.save", "Enregistrer")}
              </button>
            </div>
          )}

          {activeForm === "warningChangeValueCO" && (
            <div className={styles.containerModal}>
              <BsFillExclamationTriangleFill className={styles.iconWarning} />
              <h2>{t("common.confirm_update", "Confirmer la mise à jour")}</h2>
              <p>{t("co.serial", "N° Série CO")}: {currentUpdate?.serial}</p>
              <p>{t("co.soundcard_serial", "N° Série Carte Son")}: {currentUpdate?.soundcard_serial}</p>
              <p>{t("co.vocal", "Vocal")}: {currentUpdate?.calib_vocal}</p>
              <p>{t("co.tonal_values", "Valeurs tonales")}: {currentUpdate?.calib_tonal}</p>
              <button
                type="button"
                className={styles.modalSaveBtn}
                onClick={confirmUpdate}
              >
                {t("common.confirm", "Confirmer")}
              </button>
            </div>
          )}
          {activeForm === "confirmDeleteCO" && (
            <div className={styles.containerModal}>
              <FiTrash2 style={{ color: "#ef4444", fontSize: "2.1em" }} />
              <h2>{t("co.confirm_delete", "Confirmer la suppression")}</h2>
              <p>
                {t("co.delete_warning", "Êtes-vous sûr de vouloir supprimer la calibration CO")} : <strong>{currentUpdate?.serial}</strong> ?
              </p>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.modalSaveBtn}
                  onClick={confirmDelete}
                >
                  {t("co.delete", "Supprimer")}
                </button>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={customToggle}
                >
                  {t("common.cancel", "Annuler")}
                </button>
              </div>
            </div>
          )}

          {activeForm === "confirmUnlinkCabine" && (
            <div className={styles.containerModal}>
              <BsFillExclamationTriangleFill className={styles.iconWarning} />
              <h2>{t("co.confirm_unlink", "Confirmer la suppression de la liaison")}</h2>
              <p>
                {t(
                  "co.unlink_warning",
                  "Voulez-vous supprimer la liaison entre la CO"
                )}{" "}
                <strong>{currentUpdate?.serial}</strong>{" "}
                {t("co.unlink_warning_and", "et la cabine")}{" "}
                <strong>{currentUpdate?._cabName || `Cab #${currentUpdate?._cabId}`}</strong> ?
              </p>
              <p>
                {t(
                  "co.unlink_info",
                  "La calibration CO ne sera pas supprimée, seule la liaison avec la cabine sera retirée."
                )}
              </p>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.modalSaveBtn}
                  onClick={confirmUnlink}
                >
                  {t("co.unlink", "Délier")}
                </button>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={customToggle}
                >
                  {t("common.cancel", "Annuler")}
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </>
  );
};

export default ManageCO;
